import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaEnvelope, FaPhone, FaBirthdayCake, FaCalendarAlt, FaStar, FaBriefcase, FaCamera, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DatePicker, { registerLocale } from 'react-datepicker';
import { de } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import OnboardingProgressBar from './OnboardingProgressBar';
import EmployeeOverviewTab from './EmployeeOverviewTab';
import EmployeeProfileTab from './EmployeeProfileTab';
import EmployeeDocumentsTab from './EmployeeDocumentsTab';
import EmployeeRolesTab from './EmployeeRolesTab';
import EmployeeTimeTrackingTab from './EmployeeTimeTrackingTab';
import { PhotoEditorModal } from './PhotoEditorModal';
import { useEmployeeDetailData } from '../hooks/useEmployeeDetailData';
import { EMPLOYMENT_TYPE_OPTIONS } from '../utils/constants';
import EmployeeService from '../services/EmployeeService';
import WorkAreaService from '../../workareas/services/WorkAreaService';
import httpService from '../../../core/services/HttpService';
import { absolutizeMediaUrl } from '../../../utils/url';
import { useUserRole } from '../../../hooks/useUserRole';
import { personalakteStyles as ps, colors } from '../styles/personalakteStyles';

type TabType = 'overview' | 'profile' | 'documents' | 'roles' | 'timeTracking';

const EmployeeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { isAdmin, isManager, permissions: userPermissions } = useUserRole();
  const canViewProfile = isAdmin || userPermissions.can_view_profile;
  const canViewDocuments = isAdmin || userPermissions.can_view_documents;
  const canViewRolesTab = isAdmin;
  const restrictedTabs: TabType[] = [
    ...(!canViewProfile ? ['profile' as TabType] : []),
    ...(!canViewDocuments ? ['documents' as TabType] : []),
    ...(!canViewRolesTab ? ['roles' as TabType] : []),
  ];
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const validTabs: TabType[] = ['overview', 'profile', 'documents', 'roles', 'timeTracking'];
    if (tabParam && validTabs.includes(tabParam as TabType)) {
      return tabParam as TabType;
    }
    return 'overview';
  });
  const [highlightField, setHighlightField] = useState<string | null>(null);
  const [localProfileOverride, setLocalProfileOverride] = useState<any>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoEditorOpen, setPhotoEditorOpen] = useState(false);
  const [photoLoadError, setPhotoLoadError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [allWorkAreas, setAllWorkAreas] = useState<any[]>([]);
  const [togglingWA, setTogglingWA] = useState<number | null>(null);
  const [localWorkAreaIds, setLocalWorkAreaIds] = useState<Set<number> | null>(null);

  const employeeId = id ? parseInt(id, 10) : 0;
  const { overview, profile, header, loading, error, refetch, refetchProfile, refetchOverview } = useEmployeeDetailData(employeeId, { skipProfile: !canViewProfile });

  useEffect(() => {
    if (restrictedTabs.includes(activeTab)) {
      setActiveTab('overview');
    }
  }, [isAdmin, isManager, userPermissions.can_view_profile, userPermissions.can_view_documents]);

  useEffect(() => {
    WorkAreaService.fetchWorkAreas()
      .then(was => setAllWorkAreas(was))
      .catch(() => {});
  }, []);

  const stablePhotoUrl = React.useMemo(() => {
    if (!header?.photoUrl) return undefined;
    return `${absolutizeMediaUrl(header.photoUrl)}?_ts=${Date.now()}`;
  }, [header?.photoUrl]);

  React.useEffect(() => {
    setLocalProfileOverride(prev => {
      if (!prev) return null;
      // Profile endpoint doesn't return uniform fields — preserve them from override
      const uniform: Record<string, any> = {};
      if (prev.clothing_size) uniform.clothing_size = prev.clothing_size;
      if (prev.clothing_cut) uniform.clothing_cut = prev.clothing_cut;
      if (prev.has_received_uniform != null) uniform.has_received_uniform = prev.has_received_uniform;
      if (Object.keys(uniform).length === 0) return null;
      return { ...profile.data, ...uniform };
    });
  }, [profile.data]);

  React.useEffect(() => {
    if (header?.photoUrl) {
      setPhotoLoadError(false);
      const absoluteUrl = absolutizeMediaUrl(header.photoUrl);

      const img = new Image();
      img.onload = () => {
        setPhotoLoadError(false);
      };
      img.onerror = () => {
        console.error('[EmployeeDetail] Failed to load photo:', absoluteUrl);
        setPhotoLoadError(true);
      };
      img.src = absoluteUrl || '';
    } else {
      setPhotoLoadError(false);
    }
  }, [header?.photoUrl]);

  const backHref = location.state?.from || `/employees${location.search || ''}`;


  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      INVITED: 'Invited',
      WAITING_FOR_MANAGER: 'Waiting for Manager',
      ACTIVE: 'Active',
      DEACTIVATED: 'Deactivated',
      PENDING_DEACTIVATION: 'Pending Deactivation',
    };
    return statusMap[status] || status;
  };

  const getEmploymentTypeLabel = (employmentType: string): string => {
    const option = EMPLOYMENT_TYPE_OPTIONS.find(opt => opt.value === employmentType);
    return option ? option.label : employmentType;
  };

  const getStatusStyle = (status: string): { bg: string; color: string } => {
    const styleMap: Record<string, { bg: string; color: string }> = {
      INVITED: { bg: colors.warningLight, color: colors.warning },
      WAITING_FOR_MANAGER: { bg: colors.warningLight, color: colors.warning },
      ACTIVE: { bg: colors.successLight, color: colors.success },
      DEACTIVATED: { bg: colors.dangerLight, color: colors.danger },
      PENDING_DEACTIVATION: { bg: colors.warningLight, color: colors.warning },
    };
    return styleMap[status] || { bg: '#f1f5f9', color: colors.textMuted };
  };

  const canDeactivate = (status: string): boolean => {
    return status === 'ACTIVE';
  };

  const canReactivate = (status: string): boolean => {
    return status === 'DEACTIVATED';
  };

  const canInvite = (status: string): boolean => {
    return status === 'INVITED' || status === 'WAITING_FOR_MANAGER';
  };

  const isPillClickable = (status: string): boolean => {
    return canDeactivate(status) || canReactivate(status) || status === 'PENDING_DEACTIVATION';
  };

  const [statusChanging, setStatusChanging] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [deactivateDate, setDeactivateDate] = useState<Date | null>(null);

  registerLocale('de', de);

  const handlePillClick = useCallback(() => {
    if (!header) return;
    if (canDeactivate(header.onboardingStatus) || header.onboardingStatus === 'PENDING_DEACTIVATION') {
      setDeactivateDate(null);
      setShowStatusModal(true);
    } else if (canReactivate(header.onboardingStatus)) {
      setShowStatusModal(true);
    }
  }, [header]);

  const confirmDeactivateImmediate = useCallback(async () => {
    setShowStatusModal(false);
    setStatusChanging(true);
    try {
      await httpService.post(`/api/admin/employees/${employeeId}/deactivate/`);
      toast.success('Mitarbeiter deaktiviert');
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || e?.message || 'Fehler beim Deaktivieren');
    } finally {
      setStatusChanging(false);
    }
  }, [employeeId, refetch]);

  const confirmDeactivateScheduled = useCallback(async () => {
    if (!deactivateDate) return;
    setShowStatusModal(false);
    setStatusChanging(true);
    try {
      const dateStr = deactivateDate.toISOString().split('T')[0];
      await httpService.post(`/api/admin/employees/${employeeId}/deactivate/`, { deactivate_at: dateStr });
      toast.success(`Deaktivierung geplant für ${deactivateDate.toLocaleDateString('de-DE')}`);
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || e?.message || 'Fehler beim Planen der Deaktivierung');
    } finally {
      setStatusChanging(false);
    }
  }, [employeeId, deactivateDate, refetch]);

  const handleCancelDeactivation = useCallback(async () => {
    setShowStatusModal(false);
    setStatusChanging(true);
    try {
      await httpService.post(`/api/admin/employees/${employeeId}/cancel-deactivation/`);
      toast.success('Geplante Deaktivierung abgebrochen');
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || e?.message || 'Fehler beim Abbrechen');
    } finally {
      setStatusChanging(false);
    }
  }, [employeeId, refetch]);

  const handleReactivate = useCallback(async () => {
    setShowStatusModal(false);
    setStatusChanging(true);
    try {
      await httpService.post(`/api/admin/employees/${employeeId}/reactivate/`);
      toast.success('Mitarbeiter reaktiviert');
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || e?.message || 'Fehler beim Reaktivieren');
    } finally {
      setStatusChanging(false);
    }
  }, [employeeId, refetch]);

  const handleInvite = useCallback(async () => {
    setStatusChanging(true);
    try {
      await httpService.post(`/api/admin/employees/${employeeId}/activate/`);
      toast.success('Einladung gesendet');
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || e?.message || 'Fehler beim Einladen');
    } finally {
      setStatusChanging(false);
    }
  }, [employeeId, refetch]);

  const handleToggleWorkArea = useCallback(async (waId: number, isAssigned: boolean) => {
    if (!header || header.onboardingStatus === 'DEACTIVATED') return;
    setTogglingWA(waId);

    const currentIds = localWorkAreaIds ?? new Set(header.workAreas.map(w => w.id));
    const optimisticIds = new Set(currentIds);
    if (isAssigned) {
      optimisticIds.delete(waId);
    } else {
      optimisticIds.add(waId);
    }
    setLocalWorkAreaIds(optimisticIds);

    try {
      if (isAssigned) {
        await WorkAreaService.unassignEmployee(waId, { employee_id: employeeId });
      } else {
        await WorkAreaService.assignEmployee(waId, { employee_id: employeeId });
      }
      await refetchOverview();
      setLocalWorkAreaIds(null);
    } catch (e: any) {
      setLocalWorkAreaIds(currentIds);
      toast.error(e?.data?.detail || e?.message || 'Fehler beim Ändern der Work Area');
    } finally {
      setTogglingWA(null);
    }
  }, [employeeId, header, localWorkAreaIds, refetchOverview]);

  const handleSetPrimaryWorkArea = useCallback(async (waId: number) => {
    if (!header || header.onboardingStatus === 'DEACTIVATED') return;
    try {
      await EmployeeService.updateEmployeeProfile(employeeId, {
        job: { primary_work_area: waId },
      } as any);
      toast.success('Primary Work Area gesetzt');
      await refetch();
    } catch (e: any) {
      toast.error(e?.data?.detail || e?.message || 'Fehler beim Setzen der Primary Work Area');
    }
  }, [employeeId, header, refetch]);

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handlePhotoSave = async (croppedBlob: Blob) => {
    setPhotoEditorOpen(false);
    setUploadingPhoto(true);

    try {
      const file = new File([croppedBlob], 'photo.jpg', { type: 'image/jpeg' });

      const response = await EmployeeService.uploadEmployeePhoto(employeeId, file);

      toast.success('Foto erfolgreich hochgeladen');

      await refetch();
    } catch (err: any) {
      console.error('[PhotoUpload] Failed to upload photo:', err);
      console.error('[PhotoUpload] Error response:', err?.response?.data);
      console.error('[PhotoUpload] Error status:', err?.response?.status);

      const message = err?.response?.data?.detail || err?.message || 'Upload fehlgeschlagen';
      toast.error(`Fehler beim Hochladen: ${message}`);
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePhotoDelete = async () => {
    setPhotoEditorOpen(false);
    setUploadingPhoto(true);

    try {
      await EmployeeService.deleteEmployeePhoto(employeeId);
      toast.success('Foto erfolgreich gelöscht');
      await refetch();
    } catch (err: any) {
      console.error('Failed to delete photo:', err);
      const message = err?.response?.data?.detail || err?.message || 'Löschen fehlgeschlagen';
      toast.error(`Fehler beim Löschen: ${message}`);
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: 32,
        textAlign: 'center',
        minHeight: '100vh',
        background: colors.bgPage,
      }}>
        <p style={{ color: colors.textMuted }}>Loading employee data...</p>
      </div>
    );
  }

  if (!overview.data && !profile.data && !loading) {
    const errorMessage = error
      ? `${(error as any)?.response?.data?.detail || (error as any)?.message || 'Failed to load employee data'}`
      : 'Employee not found';

    return (
      <div style={{
        padding: 32,
        minHeight: '100vh',
        background: colors.bgPage,
      }}>
        <Link
          to={backHref}
          style={ps.backButton}
        >
          <FaArrowLeft size={14} />
          Back to Employees
        </Link>
        <p style={{ color: colors.danger, fontSize: 14 }}>{errorMessage}</p>
      </div>
    );
  }

  const primaryWorkArea = header?.workAreas?.find(wa => wa.is_primary);
  const employee = overview.data;

  const renderErrorBanner = (message: string) => (
    <div style={{
      padding: 16,
      background: colors.warningLight,
      border: `1px solid ${colors.warning}`,
      borderRadius: 8,
      marginBottom: 16,
      fontSize: 14,
      color: '#92400e',
    }}>
      {message}
    </div>
  );

  const statusStyle = header ? getStatusStyle(header.onboardingStatus) : { bg: '#f1f5f9', color: colors.textMuted };

  return (
    <div style={ps.pageContainer}>
      <div>
      <Link
        to={backHref}
        className="hover-back"
        style={ps.backButton}
      >
        <FaArrowLeft size={14} />
        Back to Employees
      </Link>

      {overview.error && renderErrorBanner('Failed to load employee overview data')}
      {profile.error && renderErrorBanner('Failed to load employee profile data')}

      {!header && (
        <div style={{ padding: 32, textAlign: 'center', color: colors.textMuted }}>
          <p>Loading employee header...</p>
        </div>
      )}

      {header && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div
            style={{
              ...ps.card,
              padding: 24,
            }}
          >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 16, flex: 1 }}>
              <div
                onClick={() => !uploadingPhoto && setPhotoEditorOpen(true)}
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: 44,
                  background: (stablePhotoUrl && !photoLoadError)
                    ? `url(${stablePhotoUrl})`
                    : `linear-gradient(135deg, ${colors.bgMuted} 0%, ${colors.border} 100%)`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 36,
                  fontWeight: 700,
                  color: colors.slate,
                  cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  transition: 'opacity 0.2s ease',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  if (!uploadingPhoto) {
                    e.currentTarget.style.opacity = '0.85';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                title={header.photoUrl ? 'Foto bearbeiten' : 'Foto hochladen'}
              >
                {(!header.photoUrl || photoLoadError) && !uploadingPhoto && (
                  <FaUser size={36} color={colors.textLight} />
                )}
                {uploadingPhoto && (
                  <div style={{ fontSize: 12, color: colors.textMuted }}>...</div>
                )}
              </div>

            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: 24, fontWeight: 700, color: colors.navy, margin: '0 0 10px 0' }}>
                {header.fullName}
              </h1>

              <div style={{ display: 'flex', gap: 20, marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaEnvelope color={colors.textLight} size={13} />
                  <span style={{ fontSize: 14, color: colors.textBody }}>
                    {header.email || '—'}
                    {!header.email && (
                      <>
                        {' · '}
                        <button
                          onClick={() => { setActiveTab('profile'); setHighlightField('email'); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: colors.slate,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: 14,
                            padding: 0,
                            fontWeight: 500,
                          }}
                        >
                          Jetzt ergänzen
                        </button>
                      </>
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaPhone color={colors.textLight} size={13} />
                  <span style={{ fontSize: 14, color: colors.textBody }}>
                    {header.phone || '—'}
                    {!header.phone && (
                      <>
                        {' · '}
                        <button
                          onClick={() => { setActiveTab('profile'); setHighlightField('phone'); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: colors.slate,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: 14,
                            padding: 0,
                            fontWeight: 500,
                          }}
                        >
                          Jetzt ergänzen
                        </button>
                      </>
                    )}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 20, marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaBirthdayCake color={colors.textLight} size={13} />
                  <span style={{ fontSize: 13, color: colors.textMuted }}>
                    <strong style={{ color: colors.textBody }}>Geburtsdatum:</strong> {formatDate(header.birthDate)}
                    {!header.birthDate && (
                      <>
                        {' · '}
                        <button
                          onClick={() => { setActiveTab('profile'); setHighlightField('birth_date'); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: colors.slate,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: 13,
                            padding: 0,
                            fontWeight: 500,
                          }}
                        >
                          Jetzt ergänzen
                        </button>
                      </>
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaCalendarAlt color={colors.textLight} size={13} />
                  <span style={{ fontSize: 13, color: colors.textMuted }}>
                    <strong style={{ color: colors.textBody }}>Startdatum:</strong> {formatDate(header.startDate)}
                    {!header.startDate && (
                      <>
                        {' · '}
                        <button
                          onClick={() => { setActiveTab('profile'); setHighlightField('start_date'); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: colors.slate,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: 13,
                            padding: 0,
                            fontWeight: 500,
                          }}
                        >
                          Jetzt ergänzen
                        </button>
                      </>
                    )}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 20, marginBottom: 14, flexWrap: 'wrap' }}>
                {header.employmentType && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaBriefcase color={colors.textLight} size={13} />
                    <span style={{ fontSize: 13, color: colors.textMuted }}>
                      <strong style={{ color: colors.textBody }}>Beschäftigungsart:</strong> {getEmploymentTypeLabel(header.employmentType)}
                    </span>
                  </div>
                )}
                {header.positionTitle && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaBriefcase color={colors.textLight} size={13} />
                    <span style={{ fontSize: 13, color: colors.textMuted }}>
                      <strong style={{ color: colors.textBody }}>Position:</strong> {header.positionTitle}
                    </span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                {allWorkAreas.map((wa) => {
                  const assignedIds = localWorkAreaIds ?? new Set(header.workAreas.map(w => w.id));
                  const isAssigned = assignedIds.has(wa.id);
                  const isPrimary = header.workAreas.find(w => w.id === wa.id)?.is_primary;
                  const isDeactivated = header.onboardingStatus === 'DEACTIVATED';
                  const isToggling = togglingWA === wa.id;
                  const waColor = wa.color || colors.slate;

                  return (
                    <button
                      key={wa.id}
                      disabled={isDeactivated || isToggling}
                      onClick={() => handleToggleWorkArea(wa.id, isAssigned)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '5px 14px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        border: isAssigned ? 'none' : `1px solid ${colors.border}`,
                        background: isDeactivated
                          ? (isAssigned ? '#e2e8f0' : '#f8fafc')
                          : (isAssigned ? waColor : '#f1f5f9'),
                        color: isDeactivated
                          ? '#94a3b8'
                          : (isAssigned ? '#fff' : '#94a3b8'),
                        cursor: isDeactivated ? 'default' : 'pointer',
                        opacity: isToggling ? 0.5 : (isDeactivated ? 0.6 : 1),
                        transition: 'all 0.2s ease',
                        filter: isDeactivated ? 'grayscale(100%)' : 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!isDeactivated && !isToggling) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = `0 3px 8px ${waColor}40`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {isAssigned && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isPrimary && !isDeactivated) handleSetPrimaryWorkArea(wa.id);
                          }}
                          title={isPrimary ? 'Primary Work Area' : 'Als Primary setzen'}
                          style={{
                            cursor: isPrimary || isDeactivated ? 'default' : 'pointer',
                            display: 'inline-flex',
                            opacity: isPrimary ? 1 : 0.4,
                          }}
                        >
                          <FaStar size={10} />
                        </span>
                      )}
                      {wa.name}
                    </button>
                  );
                })}
              </div>

              {primaryWorkArea ? (
                header.manager ? (
                  (() => {
                    const m = header.manager as any;
                    const mgrName =
                      ((m.first_name || m.firstName || '') + ' ' +
                       (m.last_name || m.lastName || '')).trim();

                    const mgrDisplayName =
                      mgrName.length > 0
                        ? mgrName
                        : (m.name || m.display_name ||
                           (m.email ? m.email.split('@')[0] : '—'));

                    const managerId = m.id || m.employee_id || m.user_id;

                    return (
                      <div style={{ fontSize: 13, color: colors.textMuted }}>
                        <div style={{ marginBottom: 4 }}>
                          <strong style={{ color: colors.textBody }}>Manager:</strong>{' '}
                          {managerId ? (
                            <Link
                              to={`/employees/${managerId}`}
                              style={{
                                color: colors.slate,
                                textDecoration: 'none',
                                fontWeight: 500,
                                transition: 'color 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.textDecoration = 'underline';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.textDecoration = 'none';
                              }}
                            >
                              {mgrDisplayName}
                            </Link>
                          ) : (
                            <span>{mgrDisplayName}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20, fontSize: 13, color: colors.textMuted }}>
                          {m.email && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaEnvelope size={11} color={colors.textLight} />
                              <a href={`mailto:${m.email}`} style={{ color: colors.slate, textDecoration: 'none' }}>
                                {m.email}
                              </a>
                            </div>
                          )}
                          {m.phone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaPhone size={11} color={colors.textLight} />
                              <span>{m.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div style={{ fontSize: 13, color: colors.textMuted }}>
                    <strong style={{ color: colors.textBody }}>Manager:</strong> —{' '}
                    <span style={{ fontSize: 12, color: colors.textLight }}>
                      (Kein Manager hinterlegt)
                    </span>
                  </div>
                )
              ) : (
                <div style={{ fontSize: 13, color: colors.textMuted }}>
                  <strong style={{ color: colors.textBody }}>Manager:</strong>{' '}
                  <span style={{ fontSize: 12, color: colors.textLight }}>
                    Klicke <FaStar size={9} style={{ verticalAlign: 'middle', color: colors.slate }} /> bei einer Work Area um Primary zu setzen
                  </span>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12 }}>
            <span
              onClick={isPillClickable(header.onboardingStatus) ? handlePillClick : undefined}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 20px',
                borderRadius: 9999,
                fontSize: 13,
                fontWeight: 600,
                lineHeight: '20px',
                background: '#fff',
                color: statusStyle.color,
                border: `1.5px solid ${statusStyle.color}`,
                cursor: isPillClickable(header.onboardingStatus) ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (isPillClickable(header.onboardingStatus)) {
                  e.currentTarget.style.background = statusStyle.bg;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
              }}
            >
              {getStatusLabel(header.onboardingStatus)}
            </span>

            {canInvite(header.onboardingStatus) && (
              <button
                onClick={handleInvite}
                disabled={statusChanging}
                style={statusChanging ? ps.buttonDisabled : ps.buttonPrimary}
              >
                {statusChanging ? 'Wird gesendet…' : (header.onboardingStatus === 'INVITED' ? 'Erneut einladen' : 'Einladen')}
              </button>
            )}
          </div>
        </div>
        </div>

        <OnboardingProgressBar milestones={header.onboardingMilestones} />
        </div>

        <div
          style={{
            ...ps.card,
            padding: 0,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              borderBottom: `1px solid ${colors.border}`,
              padding: '0 16px',
              gap: 0,
            }}
          >
            {[
              { key: 'overview' as TabType, label: 'Overview' },
              { key: 'profile' as TabType, label: 'Profile' },
              { key: 'timeTracking' as TabType, label: 'Time Tracking' },
              { key: 'documents' as TabType, label: 'Documents' },
              { key: 'roles' as TabType, label: 'Roles & Access' },
            ].filter((tab) => !restrictedTabs.includes(tab.key)).map((tab) => (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                aria-current={activeTab === tab.key ? 'page' : undefined}
                className={activeTab !== tab.key ? 'hover-tab' : undefined}
                style={ps.tabButton(activeTab === tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div style={{ padding: 16 }}>
            {activeTab === 'overview' && employee && <EmployeeOverviewTab employee={employee} employeeId={employeeId} profile={profile.data} onTabChange={setActiveTab} />}
            {activeTab === 'timeTracking' && employee && (
              <EmployeeTimeTrackingTab employeeId={employeeId} employee={employee} />
            )}
            {activeTab === 'profile' && !restrictedTabs.includes('profile') && (
              <EmployeeProfileTab
                employeeId={header.id}
                initialProfile={localProfileOverride || profile.data || {}}
                onProfileChanged={(nextProfile) => {
                  setLocalProfileOverride(nextProfile);
                }}
                refetchProfile={refetchProfile}
                refetchOverview={refetchOverview}
                highlightField={highlightField}
                onHighlightDone={() => setHighlightField(null)}
              />
            )}
            {activeTab === 'documents' && !restrictedTabs.includes('documents') && (
              <EmployeeDocumentsTab
                employeeId={header.id}
                employeeData={{
                  employment_type: overview.data?.job?.employment_type || profile.data?.job?.employment_type,
                  primary_work_area: overview.data?.primary_work_area || profile.data?.job?.primary_work_area,
                  work_areas: overview.data?.work_areas || header?.workAreas,
                  start_date: overview.data?.job?.start_date || profile.data?.job?.start_date,
                  person: {
                    first_name: overview.data?.employee?.first_name || profile.data?.person?.first_name,
                    last_name: overview.data?.employee?.last_name || profile.data?.person?.last_name,
                    email: header.email,
                  },
                }}
              />
            )}
            {activeTab === 'roles' && (
              <EmployeeRolesTab
                employeeId={header.id}
                employeeStatus={header.onboardingStatus}
                employeeData={{
                  first_name: overview.data?.employee?.first_name || profile.data?.person?.first_name || '',
                  last_name: overview.data?.employee?.last_name || profile.data?.person?.last_name || '',
                  email: header.email || '',
                }}
              />
            )}
          </div>
        </div>
        </>
      )}

      {showStatusModal && header && (
        <div
          onClick={() => setShowStatusModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              maxWidth: 480,
              width: '90%',
              padding: 32,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }}
          >
            {/* --- ACTIVE / INACTIVE: Deactivate modal --- */}
            {canDeactivate(header.onboardingStatus) && (
              <>
                <p style={{ fontSize: 14, color: colors.textMuted, margin: '0 0 4px 0' }}>
                  <strong style={{ color: colors.textBody }}>{header.fullName}</strong>
                  {header.startDate
                    ? ` — Active since ${formatDate(header.startDate)}`
                    : ' — Active'}
                </p>

                <h2 style={{ fontSize: 20, fontWeight: 600, color: colors.navy, margin: '0 0 20px 0' }}>
                  Deactivate Access?
                </h2>

                <div style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  marginBottom: 24,
                  fontSize: 13,
                  color: colors.textMuted,
                  lineHeight: 1.6,
                }}>
                  <FaExclamationTriangle
                    size={16}
                    color={colors.warning}
                    style={{ flexShrink: 0, marginTop: 2, animation: 'pulse-warning 2s ease-in-out infinite' }}
                  />
                  <span>
                    With deactivation the user will lose access to the employee app and will not see shifts or apply for shifts anymore. When Payroll is activated, the employment will be reported as terminated.
                  </span>
                </div>
                <style>{`
                  @keyframes pulse-warning {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.4; }
                  }
                `}</style>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                  <div style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                  }}>
                    <div style={{ flex: 1 }}>
                      <DatePicker
                        selected={deactivateDate}
                        onChange={(date: Date | null) => setDeactivateDate(date)}
                        locale="de"
                        dateFormat="dd.MM.yyyy"
                        minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                        placeholderText="Datum wählen…"
                        portalId="datepicker-portal"
                        customInput={
                          <button
                            style={{
                              padding: '10px 16px',
                              fontSize: 13,
                              fontWeight: 500,
                              border: `1px solid ${colors.border}`,
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: '#fff',
                              color: deactivateDate ? colors.textBody : colors.textMuted,
                              width: '100%',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <FaCalendarAlt size={13} color={colors.textLight} />
                            {deactivateDate
                              ? deactivateDate.toLocaleDateString('de-DE')
                              : 'Set last working day…'}
                          </button>
                        }
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, height: 1, background: colors.border }} />
                    <span style={{ fontSize: 12, color: colors.textLight }}>or</span>
                    <div style={{ flex: 1, height: 1, background: colors.border }} />
                  </div>

                  <button
                    onClick={confirmDeactivateImmediate}
                    style={{
                      padding: '10px 20px',
                      fontSize: 13,
                      fontWeight: 600,
                      border: `1.5px solid ${colors.danger}`,
                      borderRadius: 9999,
                      cursor: 'pointer',
                      background: '#fff',
                      color: colors.danger,
                      transition: 'all 0.2s ease',
                      width: '100%',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.dangerLight; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                  >
                    Deactivate immediately
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {deactivateDate && (
                    <button
                      onClick={confirmDeactivateScheduled}
                      style={{
                        ...ps.buttonPrimary,
                        width: '100%',
                        justifyContent: 'center',
                        background: colors.slate,
                        boxShadow: 'none',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                    >
                      Schedule
                    </button>
                  )}
                  <button
                    onClick={() => setShowStatusModal(false)}
                    style={{
                      ...ps.buttonSecondary,
                      width: '100%',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgMuted; e.currentTarget.style.borderColor = colors.textLight; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = colors.bgCard; e.currentTarget.style.borderColor = colors.border; }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {/* --- PENDING_DEACTIVATION: Cancel scheduled deactivation --- */}
            {header.onboardingStatus === 'PENDING_DEACTIVATION' && (
              <>
                <h2 style={{ fontSize: 20, fontWeight: 600, color: colors.navy, margin: '0 0 12px 0' }}>
                  Pending Deactivation
                </h2>

                <div style={{
                  background: colors.warningLight,
                  borderRadius: 8,
                  padding: 16,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  marginBottom: 20,
                }}>
                  <FaExclamationTriangle size={18} color={colors.warning} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: 14, color: colors.warning, lineHeight: 1.5 }}>
                    <strong>{header.fullName}</strong> is scheduled for deactivation
                    {header.scheduledDeactivationDate
                      ? ` on ${formatDate(header.scheduledDeactivationDate)}`
                      : ''}.
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <button
                    onClick={() => setShowStatusModal(false)}
                    style={ps.buttonSecondary}
                  >
                    Close
                  </button>
                  <button
                    onClick={handleCancelDeactivation}
                    style={{
                      padding: '10px 24px',
                      fontSize: 14,
                      fontWeight: 600,
                      border: 'none',
                      borderRadius: 9999,
                      cursor: 'pointer',
                      background: colors.success,
                      color: '#fff',
                      transition: 'opacity 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                  >
                    Cancel Deactivation
                  </button>
                </div>
              </>
            )}

            {/* --- DEACTIVATED: Reactivate modal --- */}
            {canReactivate(header.onboardingStatus) && (
              <>
                <h2 style={{ fontSize: 20, fontWeight: 600, color: colors.navy, margin: '0 0 12px 0' }}>
                  Reactivate Employee?
                </h2>

                <p style={{ fontSize: 14, color: colors.textBody, margin: '0 0 24px 0', lineHeight: 1.5 }}>
                  <strong>{header.fullName}</strong> will regain access to the App.
                </p>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <button
                    onClick={() => setShowStatusModal(false)}
                    style={ps.buttonSecondary}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReactivate}
                    style={{
                      ...ps.buttonPrimary,
                      borderRadius: 9999,
                    }}
                  >
                    Reactivate
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <PhotoEditorModal
        isOpen={photoEditorOpen}
        currentPhotoUrl={absolutizeMediaUrl(header?.photoUrl) || null}
        onClose={() => setPhotoEditorOpen(false)}
        onSave={handlePhotoSave}
        onDelete={handlePhotoDelete}
      />
      </div>
    </div>
  );
};

export default EmployeeDetail;
